import { useState } from 'react';
import type { ConsultaCifras, Indicador } from '../../api/ops/indicadores';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { Grafico } from './Grafico';
import type { PropsApartado } from './Indicadores';
import {
    calorDe,
    cambioDe,
    escribir,
    etiquetaPunto,
    fraseCambio,
    mejorSiSube,
    motivoIncompleto,
    nivelCalor,
    texto,
    textoComparacion,
    tonoDelCambio,
    valorDibujable,
    type Filtro,
} from './reglas';
import { useCifras } from './useCifras';

/**
 * «Lo importante» de 7.2 (José, 16 oct): 3 o 4 cifras clave en grande con su chip, una frase llana y, debajo, un gráfico
 * ancho con la evolución de la que se elija. Las cifras clave son las de la tarjeta «Periodo» de `getDashboard` (lo que
 * los manifiestos declaran para el panel de inicio, `importantes`); el periodo, la sede y la comparación son los filtros
 * de 7.2. El color del chip solo si el indicador dice si subir es bueno (pregunta 324); si no, gris.
 */
export function LoImportante(props: PropsApartado & { lista: readonly Indicador[]; consulta: ConsultaCifras | null; filtro: Filtro; linea: string; onAbrir: (codigo: string) => void }) {
    const { lista, consulta, linea, onAbrir } = props;
    const [elegido, setElegido] = useState<string | null>(null);
    const activo = lista.find((i) => i.code === elegido) ?? lista[0] ?? null;
    if (lista.length === 0 || activo === null) {
        return null;
    }
    return (
        <section className="rv-ind-top" aria-labelledby="rv-ind-top">
            <div className="rv-ind-top__cab">
                <h2 id="rv-ind-top">Lo importante</h2>
                <span className="rv-ind-gris">{linea}</span>
            </div>
            <div className="rv-ind-top__cifras">
                {lista.map((i) => (
                    <CifraClave key={i.code} {...props} indicador={i} marcada={lista.length > 1 && i.code === activo.code} onAbrir={() => { onAbrir(i.code); }} />
                ))}
            </div>
            <Evolucion {...props} indicador={activo} consulta={consulta} lista={lista} onElegir={setElegido} />
        </section>
    );
}

function CifraClave({ ops, negocio, indicador, consulta, filtro, marcada, onAbrir }: PropsApartado & { indicador: Indicador; consulta: ConsultaCifras | null; filtro: Filtro; marcada: boolean; onAbrir: () => void }) {
    const { cifras, error } = useCifras(ops, negocio, indicador.code, consulta);
    const serie = cifras?.series.find((s) => (s.dimension_value ?? null) === null) ?? cifras?.series[0];
    const unidad = cifras?.unit ?? indicador.unit;
    const e = escribir(unidad, serie?.total);
    const cambio = cifras === null ? null : cambioDe(unidad, cifras.comparison, serie ?? null);
    const incompleto = serie?.points.some((p) => motivoIncompleto(p) !== null) ?? false;
    const frase = fraseCambio(cambio, filtro.comparar, incompleto);
    return (
        <button type="button" className={`rv-ind-tarjeta rv-ind-top__tarjeta${marcada ? ' rv-ind-top__tarjeta--marcada' : ''}`} onClick={onAbrir} aria-label={`${indicador.name}: ${texto(unidad, serie?.total)}. ${frase} Abrir la ficha.`}>
            <span className="rv-ind-tarjeta__titulo">
                {indicador.name}
                <span className="rv-ind-vermas">
                    Ver más
                    <Icono nombre="chevron-right" />
                </span>
            </span>
            {error !== null ? (
                <span className="rv-ind-tarjeta__nota">No se han podido leer las cifras.</span>
            ) : cifras === null ? (
                <span className="rv-ind-tarjeta__nota">Cargando…</span>
            ) : (
                <>
                    <span className="rv-ind-tarjeta__cifra rv-ind-top__cifra">
                        <strong>{e.cifra}</strong>
                        {e.unidad !== '' && <small>{e.unidad}</small>}
                    </span>
                    {cambio !== null && <em className={`rv-ind-cambio rv-ind-cambio--${tonoDelCambio(cambio.sentido, mejorSiSube(indicador))}`}>{cambio.texto}</em>}
                    {frase !== '' && <span className="rv-ind-top__frase">{frase}</span>}
                </>
            )}
        </button>
    );
}

function Evolucion({ ops, negocio, indicador, consulta, lista, filtro, onElegir }: PropsApartado & { indicador: Indicador; consulta: ConsultaCifras | null; lista: readonly Indicador[]; filtro: Filtro; onElegir: (codigo: string) => void }) {
    const { cifras } = useCifras(ops, negocio, indicador.code, consulta);
    const comparacion = cifras?.comparison ?? null;
    const consultaAntes = consulta === null || comparacion === null || comparacion.mode === 'none' ? null : { ...consulta, from: comparacion.from, to: comparacion.to, compare: 'none' as const };
    const antes = useCifras(ops, negocio, indicador.code, consultaAntes).cifras;
    if (cifras === null) {
        return null;
    }
    const unidad = cifras.unit ?? indicador.unit;
    const serie = cifras.series.find((s) => (s.dimension_value ?? null) === null) ?? cifras.series[0];
    const serieAntes = antes?.series.find((s) => (s.dimension_value ?? null) === null && (serie?.currency == null || s.currency === serie.currency));
    const puntos = (serie?.points ?? []).map((p) => ({ etiqueta: etiquetaPunto(p.period_start, cifras.granularity), valor: valorDibujable(p), incompleto: motivoIncompleto(p) !== null }));
    const escribirEje = (v: number) => (unidad === 'money' ? v.toLocaleString('es-ES', { maximumFractionDigits: 0 }) : v.toLocaleString('es-ES', { maximumFractionDigits: 1 }));
    return (
        <div className="rv-ind-caja">
            <div className="rv-ind-caja__cabeza">
                {lista.length > 1 ? (
                    <Desplegable etiqueta="Evolución de" valor={indicador.code} opciones={lista.map((i) => ({ valor: i.code, texto: i.name }))} onChange={onElegir} />
                ) : (
                    <span className="rv-ind-caja__titulo">{indicador.name}</span>
                )}
                {serieAntes !== undefined && filtro.comparar !== 'none' && (
                    <span className="rv-ind-leyenda">
                        <span>Este periodo</span>
                        <span className="rv-ind-leyenda__antes">{textoComparacion(filtro.comparar).replace(/^el /, '').replace(/^./, (l) => l.toLocaleUpperCase('es'))}</span>
                    </span>
                )}
            </div>
            <Grafico puntos={puntos} anteriores={serieAntes?.points.map(valorDibujable)} escribir={escribirEje} etiqueta={`Evolución de ${indicador.name}: ${texto(unidad, serie?.total)}`} />
        </div>
    );
}

/**
 * Mapa de calor día × hora (7.2, «Cuándo llega la gente»): el primer indicador del área que se lee por hora
 * (`supports_hourly`). Las cifras por hora solo llegan para 31 días como mucho: mira las 4 últimas semanas del periodo.
 */
export function TarjetaCalor({ ops, negocio, indicador, consulta, recortado, onAbrir }: PropsApartado & { indicador: Indicador; consulta: ConsultaCifras | null; recortado: boolean; onAbrir: () => void }) {
    const { cifras, error } = useCifras(ops, negocio, indicador.code, consulta);
    const calor = cifras === null ? null : calorDe(cifras);
    if (error !== null || (cifras !== null && calor === null)) {
        return null; // sin permiso, sin cifras por hora o sin nada en esas semanas: no sale
    }
    const nota = `${indicador.name} por día y hora, de media${recortado ? ' en las 4 últimas semanas' : ''}`;
    return (
        <section className="rv-ind-caja rv-ind-calor" aria-label={nota}>
            <div className="rv-ind-caja__cabeza">
                <div>
                    <span className="rv-ind-caja__titulo">{indicador.name}</span>
                    <small className="rv-ind-gris">{`Por día y hora, de media${recortado ? ' en las 4 últimas semanas' : ''}`}</small>
                </div>
                <button type="button" className="rv-ind-vermas" onClick={onAbrir}>
                    Ver más
                    <Icono nombre="chevron-right" />
                </button>
            </div>
            {calor === null ? (
                <p className="rv-ind-tarjeta__nota">Cargando…</p>
            ) : (
                <div className="rv-ind-calor__rejilla" role="table" aria-label={nota} style={{ gridTemplateColumns: `28px repeat(${String(calor.horas.length)}, minmax(14px, 1fr))` }}>
                    <span role="columnheader" />
                    {calor.horas.map((h) => (
                        <span key={`h${String(h)}`} role="columnheader" className="rv-ind-calor__hora">
                            {String(h)}
                        </span>
                    ))}
                    {calor.filas.map((f) => (
                        <div key={f.corto} role="row" className="rv-ind-calor__fila">
                            <span role="rowheader" className="rv-ind-calor__dia" title={f.largo}>
                                {f.corto}
                            </span>
                            {f.valores.map((v, i) => (
                                <span
                                    key={`${f.corto}${String(calor.horas[i])}`}
                                    role="cell"
                                    className={`rv-ind-calor__celda rv-ind-calor__celda--${String(nivelCalor(v, calor.maximo))}`}
                                    title={`${f.largo}, ${String(calor.horas[i]).padStart(2, '0')}:00: ${v === null ? 'nada' : v.toLocaleString('es-ES', { maximumFractionDigits: 1 })}`}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
            {calor !== null && (
                <span className="rv-ind-calor__leyenda" aria-hidden="true">
                    Menos
                    {[1, 2, 3, 4].map((n) => (
                        <i key={n} className={`rv-ind-calor__celda rv-ind-calor__celda--${String(n)}`} />
                    ))}
                    Más
                </span>
            )}
        </section>
    );
}
